// Text size and colour theme for the new-style surfaces (top nav, CS Notes, Order Hist 2 and the
// ffc-page views: In Ship, Data Load, Control Page, Settings, Shipping Tool).
// Stored per browser in localStorage and applied as data attributes on <html>;
// the matching CSS variables (--ff-*) live in App.vue. Older Bulma pages ignore them.

import { reactive } from 'vue'

const STORAGE_KEY = 'ffUiPrefs'

export const THEMES = [
  { key: 'herb', label: 'Herb', swatch: '#1f7a4d' },
  { key: 'clay', label: 'Clay', swatch: '#bc5a33' },
  { key: 'slate', label: 'Slate', swatch: '#2d5fa6' },
]

export const FONT_SIZES = [
  { key: 's', label: 'Small', i18n: 'sizeSmall', scale: 0.9 },
  { key: 'm', label: 'Default', i18n: 'sizeDefault', scale: 1 },
  { key: 'l', label: 'Large', i18n: 'sizeLarge', scale: 1.1 },
  { key: 'xl', label: 'Extra large', i18n: 'sizeXLarge', scale: 1.22 },
]

// 'custom' takes its brand colour from `custom` (a #rrggbb picked in the user menu);
// App.vue derives the darker ink, paper and hairline tones from it.
export const DEFAULT_CUSTOM = '#7a4fbf'
// orderHist: which Order Hist page /orderhistory renders (router/templateSwitch.js).
export const ORDER_HIST_VARIANTS = [
  { key: 'old', i18n: 'orderHistOld' },
  { key: 'new', i18n: 'orderHistNew' },
]
// lang: language of the i18n pack (src/i18n), Vietnamese by default.
export const LANGUAGES = [
  { key: 'vi', label: 'Tiếng Việt' },
  { key: 'en', label: 'English' },
]
// bg: page background (--ff-paper), set inline on <html>. 'mist' is the default;
// 'custom' comes from bgCustom (a #rrggbb picked in the user menu).
export const BACKGROUNDS = [
  { key: 'white', i18n: 'bgWhite', swatch: '#ffffff', value: '#ffffff' },
  { key: 'stone', i18n: 'bgStone', swatch: '#e6e4e0', value: '#e6e4e0' },
  { key: 'mist', i18n: 'bgMist', swatch: '#eef1f4', value: '#eef1f4' },
]
export const DEFAULT_BG_CUSTOM = '#f4efe6'
// surface: how white cards / panels / inputs / the nav are (--ff-surface, App.vue).
export const SURFACES = [
  { key: 'white', i18n: 'surfaceWhite' },
  { key: 'soft', i18n: 'surfaceSoft' },
  { key: 'dim', i18n: 'surfaceDim' },
]
export const DEFAULT_PREFS = { theme: 'herb', font: 'm', custom: DEFAULT_CUSTOM, orderHist: 'new', lang: 'vi', bg: 'mist', bgCustom: DEFAULT_BG_CUSTOM, surface: 'white' }

const HEX_RE = /^#[0-9a-f]{6}$/i

function valid (prefs) {
  const custom = HEX_RE.test(prefs.custom || '') ? prefs.custom.toLowerCase() : DEFAULT_CUSTOM
  const known = prefs.theme === 'custom' || THEMES.some(t => t.key === prefs.theme)
  return {
    theme: known ? prefs.theme : DEFAULT_PREFS.theme,
    font: FONT_SIZES.some(f => f.key === prefs.font) ? prefs.font : DEFAULT_PREFS.font,
    custom,
    orderHist: ORDER_HIST_VARIANTS.some(v => v.key === prefs.orderHist) ? prefs.orderHist : DEFAULT_PREFS.orderHist,
    lang: LANGUAGES.some(l => l.key === prefs.lang) ? prefs.lang : DEFAULT_PREFS.lang,
    bg: prefs.bg === 'custom' || BACKGROUNDS.some(b => b.key === prefs.bg) ? prefs.bg : DEFAULT_PREFS.bg,
    bgCustom: HEX_RE.test(prefs.bgCustom || '') ? prefs.bgCustom.toLowerCase() : DEFAULT_BG_CUSTOM,
    surface: SURFACES.some(x => x.key === prefs.surface) ? prefs.surface : DEFAULT_PREFS.surface,
  }
}

export function loadUiPrefs () {
  try {
    return valid(JSON.parse(localStorage.getItem(STORAGE_KEY)) || {})
  } catch (error) {
    return { ...DEFAULT_PREFS }
  }
}

export function applyUiPrefs (prefs) {
  const p = valid(prefs || {})
  const root = document.documentElement
  root.setAttribute('data-ff-theme', p.theme)
  root.setAttribute('data-ff-font', p.font)
  root.setAttribute('lang', p.lang)
  root.setAttribute('data-ff-surface', p.surface)
  const bg = p.bg === 'custom' ? p.bgCustom : (BACKGROUNDS.find(b => b.key === p.bg) || {}).value
  if (bg) root.style.setProperty('--ff-paper', bg)
  else root.style.removeProperty('--ff-paper')
  if (p.theme === 'custom') root.style.setProperty('--ff-brand', p.custom)
  else root.style.removeProperty('--ff-brand')
  return p
}

export function saveUiPrefs (prefs) {
  const p = applyUiPrefs(prefs)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)) } catch (error) { /* private mode */ }
  Object.assign(uiState, p)
  return p
}

// One live copy for the whole app: the nav, the user menu and the route switcher all
// read it, so flipping a setting re-renders everything at once.
export const uiState = reactive(applyUiPrefs(loadUiPrefs()))
